'use client';

import Link from 'next/link';
import { useEffect, useMemo, useRef, useState } from 'react';
import { TbSearch } from 'react-icons/tb';

import { DOCS_SEARCH, type DocsSearchItem } from './docs-nav';

function normalize(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');
}

/**
 * Busca funcional do /docs: foca abre a lista (vazia mostra todas as docs),
 * filtra por título/descrição/keywords ignorando acentos, clica navega.
 * ⌘K / Ctrl+K foca · Esc fecha.
 */
export function DocsSearch({
  items = DOCS_SEARCH,
  placeholder = 'Buscar na documentação…',
}: {
  items?: DocsSearchItem[];
  placeholder?: string;
} = {}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);

  const results = useMemo(() => {
    const q = normalize(query.trim());
    if (!q) return items;
    return items.filter((it) =>
      normalize(`${it.label} ${it.description} ${it.keywords}`).includes(q),
    );
  }, [query, items]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
      if (e.key === 'Escape') {
        setOpen(false);
        inputRef.current?.blur();
      }
    }
    function onClickOutside(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    }
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onClickOutside);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onClickOutside);
    };
  }, []);

  return (
    <div ref={boxRef} className={open ? 'docsearch open' : 'docsearch'}>
      <div className="searchbox">
        <TbSearch size={17} strokeWidth={2} />
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          aria-label={placeholder}
        />
        <span className="kbd">⌘K</span>
      </div>
      {open ? (
        <div className="results">
          {results.length > 0 ? (
            results.map((r) => (
              <Link key={r.href} href={r.href as never} onClick={() => setOpen(false)}>
                <span className="rl">
                  <TbSearch size={14} strokeWidth={2} />
                  {r.label}
                </span>
                <span className="rd">{r.description}</span>
              </Link>
            ))
          ) : (
            <div className="noresult">Nada encontrado pra “{query}”.</div>
          )}
        </div>
      ) : null}
    </div>
  );
}
